import { FC, useEffect, useState } from 'react';
import { isNotEmpty, sleep } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { base64ToBlob } from '@/common/utils/ways';
import { getQiniuToken } from '@/common/api/common';
import { Bucket, bucketMappingActionUrl, bucketMappingDomain } from '@/common/enums/qiniu';
import styles from './index.module.less';
import { Button, Progress, Image } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import useUploadBlobToQiniu from './useUploadBlobToQiniu';
import V2Message from 'src/common/components/Others/V2Hint/V2Message';

interface CropBoxInfoProp { x: number, y: number, w: number, h: number }

const fullWidth = document.documentElement.clientWidth;
const fullHeight = document.documentElement.clientHeight;
const defaultCropBoxInfo: CropBoxInfoProp = { x: fullWidth * 0.1, y: fullHeight * 0.1, w: fullWidth * 0.8, h: fullHeight * 0.8 };

export interface ScreenShotProps {
  /** 绑定值 */
  value?: Array<{ name: string, url: string }>,
  /** 按钮文本 */
  buttonText?: string,
  /** 显示上传进度条 */
  showProgress?: boolean,
  /** 是否预览图片 */
  preview?: boolean,
  /** 延迟多久之后开始截图，主要用于需要关闭弹窗的场景下 */
  duration?: number,
  /** 文件保存至七牛的存储空间名，详情见 src\common\enums\qiniu.ts 内的 Bucket */
  qiniuBucket?: string,
  /** 文件上传 token，如果传入，则使用传入的 qiniuToken；如果不传，则组件内部请求获取 token */
  qiniuToken?: string,
  /** 是否组件内部请求token，默认内部请求token；
  通过设置 requestToken 以及外部主动传入 qiniuToken，可以减少一个组件内多个相同 qiniuBucket 的上传组件多次请求token的情况 */
  requestToken?: boolean,
  /** ScreenShotPlugin 配置 */
  /** 开启 webrtc 模式，截图效果更好，但是需要页面授权 */
  enableWebRtc?: boolean,
  /** 裁剪框，设置截图初始区域，值为 { x: number; y: number; w: number; h: number }  */
  cropBoxInfo?: CropBoxInfoProp,
  /** 通知父级开始截图 */
  onStart?: Function,
  /** 抛出进度 */
  onUpdatePercent?: Function,
  /** 截图完成 */
  onChange?: Function,
  /** 取消截图 */
  onCancel?: Function,
  /** 抛出截图报错 */
  onError?: Function,
}


/**
 * 截图绘制
 * @category Basic
 * @description 文档：https://www.kaisir.cn/js-screen-shot/docs/config/
 * @demo
import ScreenShot from 'src/common/components/ScreenShot/index';

const [files, setFiles] = useState<any[]>([]);
<ScreenShot value={files} onChange={setFiles} />
 */
const Component:FC<ScreenShotProps> = ({
  value = [],
  buttonText = '点击截图',
  showProgress = false,
  preview = true,
  duration = 0,
  qiniuBucket = Bucket.Banners,
  qiniuToken = '',
  requestToken = true,
  enableWebRtc = false,
  cropBoxInfo = defaultCropBoxInfo,
  onStart = null,
  onUpdatePercent = null,
  onChange = null,
  onCancel = null,
  onError = null,
}) => {

  /* state */
  const [uploadDataToken, setUploadDataToken] = useState('');
  const [disabled, setDisabled] = useState(false);

  // 上传七牛的地址 https://developer.qiniu.com/kodo/manual/1671/region-endpoint
  const actionUrl = bucketMappingActionUrl[qiniuBucket];
  // 七牛文件域名前缀
  const qiniuDomain = bucketMappingDomain[qiniuBucket || Bucket.Banners];

  const uploadBlobToQiniu = useUploadBlobToQiniu({ token: uploadDataToken, domain: qiniuDomain, actionUrl: actionUrl });

  /* hooks */
  useEffect(() => {
    window.getScript('jsScreenShot');

    methods.getQiniuToken();
  }, []);

  useEffect(() => {
    /** 抛出进度 */
    onUpdatePercent?.(uploadBlobToQiniu.percent);
  }, [uploadBlobToQiniu.percent]);


  /* methods */
  const methods = useMethods({
    /**
     * @description: 获得七牛token
     * @param {type} forceRequestToken 强制从接口请求更新token
     * @return:
     */
    async getQiniuToken(forceRequestToken = false) {
      if (!requestToken && !forceRequestToken) { // 不请求token 并且 当前非强制请求token
        setUploadDataToken(qiniuToken);
        return true;
      }

      return new Promise((resolve, reject) => {
        getQiniuToken({ bucket: qiniuBucket }).then(response => {
          setUploadDataToken(response.token);
          resolve(response.token);
        }, (e) => {
          reject(e);
        });
      });
    },

    // 更新值
    updateValue (res) {
      /* 变更后抛出截图内容 [{ name, file }] */
      onChange?.(value.concat({ name: res.name, url: res.url }));
    },

    // 取消截图
    cancel() {
      onCancel?.();
    },

    // 点击按钮开始截图
    async handleClick() {

      /* 通知父级开始截图 */
      onStart?.();
      setDisabled(true);

      await sleep(duration);

      const ScreenShotPlugin = window.screenShotPlugin;
      new ScreenShotPlugin({
        enableWebRtc: enableWebRtc,
        clickCutFullScreen: false,
        // wrcWindowMode: false,
        cropBoxInfo: Object.assign(defaultCropBoxInfo, cropBoxInfo),
        level: 99999,
        loadCrossImg: true, // 是否加载跨域图片
        completeCallback: async({ base64 }) => {
          // console.log(base64, cutInfo);
          const blob = isNotEmpty(base64) ? base64ToBlob(base64.replace(/^data:image\/png;base64,/, '')) : '';
          if (blob) {
            await uploadBlobToQiniu.start(blob).then((res) => {
              methods.updateValue(res);
            }).catch((err) => {
              console.log('err', err);
              methods.getQiniuToken(true);
              onError?.(err);
            });
          }
          setDisabled(false);
        },
        // 关闭回调
        closeCallback: () => {
          setDisabled(false);
          methods.cancel();
        },
        // 响应回调
        triggerCallback: () => {
          // 截图组件加载完毕调用此方法来完成框选区域的截图
          // plugin.completeScreenshot();
        },
        // 使用 webrtc 模式截屏时，用户点了取消或者浏览器不支持时所触发的事件
        cancelCallback: (err) => {
          setDisabled(false);
          if (err?.code === -1 && err?.msg) {
            V2Message.warning(err.msg);
          }
          methods.cancel();
        },
        // 工具栏中的保存按钮点击时触发此回调
        saveCallback: () => {
          setDisabled(false);
          methods.cancel();
        },
      });
    },
    // 删除图片
    remove(i) {
      onChange?.(value.filter((item, index) => index !== i));
      uploadBlobToQiniu.reset();
    }
  });

  return (<div>
    <Button
      type='primary'
      size='small'
      disabled={disabled || (uploadBlobToQiniu.percent > 0 && uploadBlobToQiniu.percent < 100)}
      onClick={methods.handleClick}
    >{buttonText}</Button>
    {showProgress && <Progress percent={uploadBlobToQiniu.percent} style={{ width: 600, display: 'block' }} />}
    {preview && Array.isArray(value) && !!value.length && <div className='mt-10'>
      {value.map((item, index) => (<div key={index} className={styles.V2UploadImageItem}>
        <Image
          width={100}
          height={100}
          src={item.url}
        />
        <span
          className={styles.V2UploadImageCloseSpan}
          onClick={(e) => {
            e.stopPropagation();
            methods.remove(index);
          }}
        >
          <CloseCircleFilled className={styles.V2UploadImageClose} style={{ fontSize: '14px' }}/>
        </span>
      </div>))}
    </div>}
  </div>);
};

export default Component;
